import React, { createContext } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { get, put, post } from "utils/api";
import { getApiErrorMessage } from "utils/misc";
import useFetch from "hooks/useFetch";
import { errorMessage } from "actions/message";
import useTasks from "features/tasks/use-tasks";

export const OrderProfileContext = createContext();

const OrderProfileNewProvider = ({ orderId, seed, children }) => {
  const dispatch = useDispatch();
  // A seeded provider was handed everything it renders, so it makes no requests
  // of its own. Used when many orders are rendered at once and fetching per
  // order would be thousands of round trips.
  const isSeeded = !!seed;

  const [id, setId] = React.useState(orderId);

  const fetchOrderProfileApi = useFetch({
    apiFn: orderId => get(`order-profile/${orderId}`),
    onError: () => dispatch(errorMessage("Error Fetching Order")),
    defaultValue: {}
  });

  const updateOrderProfileApi = useFetch({
    apiFn: values => put(`orders/${orderId}`, values),
    defaultValue: {},
    onSuccess: () => fetchOrderProfileApi.fetchData(orderId),
    onError: error =>
      dispatch(errorMessage(getApiErrorMessage(error, "Error updating order")))
  });

  const setOrderProfileAccessApi = useFetch({
    apiFn: orderId => post(`orders/${orderId}/access`, {})
  });

  const fetchOrderLineItemsApi = useFetch({
    apiFn: orderId => get(`orders/${orderId}/line-items`),
    defaultValue: [],
    clearDataOnFetch: false
  });

  const createOrderNoteApi = useFetch({
    apiFn: values => post(`orders/${orderId}/notes`, values),
    defaultValue: {},
    onSuccess: () => fetchOrderNotesApi.fetchData(orderId),
    onError: error =>
      dispatch(errorMessage(getApiErrorMessage(error, "Failed to create note")))
  });

  const fetchOrderNotesApi = useFetch({
    apiFn: orderId => get(`orders/${orderId}/notes`),
    defaultValue: [],
    transformResponse: response => response?.notes ?? []
  });

  const fetchOrderEquipmentApi = useFetch({
    apiFn: orderId => get(`orders/${orderId}/equipment_requested`),
    defaultValue: {
      compatibleItems: {
        patientCompatibleItems: [],
        orderCompatibleItems: [],
        patientExcludedHCPCs: []
      },
      equipment_requested: []
    }
  });

  const addOrderLineItemByIdApi = useFetch({
    apiFn: ({ product_id, addQuantity }) =>
      post(`orders/${orderId}/add_line_item_by_id`, {
        product_id,
        addQuantity
      }),
    onSuccess: () => {
      fetchOrderEquipmentApi.fetchData(orderId);
      fetchOrderLineItemsApi.fetchData(orderId);
    },
    onError: error =>
      dispatch(
        errorMessage(getApiErrorMessage(error, "Error adding line item"))
      )
  });

  const updateOrderLineItemByIdApi = useFetch({
    apiFn: values => post(`orders/${orderId}/update_line_item_by_id`, values),
    onSuccess: () => fetchOrderLineItemsApi.fetchData(orderId),
    onError: error =>
      dispatch(
        errorMessage(getApiErrorMessage(error, "Error updating line item"))
      )
  });

  const addOrderLineItemApi = useFetch({
    apiFn: ({ id, addQuantity }) =>
      post(`orders/${orderId}/add_line_item`, {
        id,
        addQuantity
      }),
    onSuccess: () => {
      fetchOrderEquipmentApi.fetchData(orderId);
      fetchOrderLineItemsApi.fetchData(orderId);
    },
    onError: error =>
      dispatch(
        errorMessage(getApiErrorMessage(error, "Error adding line item"))
      )
  });

  const updateTrackingApi = useFetch({
    apiFn: payload => post(`orders/${orderId}/add_shipping_details`, payload),
    onSuccess: () => {
      fetchOrderLineItemsApi.fetchData(orderId);
      fetchOrderProfileApi.fetchData(orderId);
    },
    onError: error =>
      dispatch(
        errorMessage(getApiErrorMessage(error, "Error updating tracking"))
      )
  });

  // JANKY hack to prevent re-fetching when orderId reference changes
  React.useEffect(() => {
    if (orderId != id) {
      setId(orderId);
    }
  }, [orderId]);

  React.useEffect(() => {
    // The caller that produced the seed already logged access for this order, so
    // skipping these does not skip the audit trail.
    if (isSeeded) return;
    fetchOrderProfileApi.fetchData(orderId);
    fetchOrderLineItemsApi.fetchData(orderId);
    fetchOrderNotesApi.fetchData(orderId);
    setOrderProfileAccessApi.fetchData(orderId);
    fetchOrderEquipmentApi.fetchData(orderId);
  }, [id, isSeeded]);

  // JANKY hack to prevent re-fetching when orderId reference changes
  const [patientId, setPatientId] = React.useState(
    fetchOrderProfileApi.response?.patient_id
  );
  React.useEffect(() => {
    if (patientId != fetchOrderProfileApi.response?.patient_id) {
      setPatientId(fetchOrderProfileApi.response?.patient_id);
    }
  }, [fetchOrderProfileApi.response?.patient_id]);

  const {
    tasks,
    createTask,
    createTaskNote,
    getPatientTasks,
    updateTask,
    isFetchingTasks
  } = useTasks(patientId);

  const patientFormAttributesAPI = useFetch({
    apiFn: patientId => get(`patients/${patientId}/form-attributes?excludeCompanyInsurances=true&excludeOrderFormSettings=true`),
    defaultValue: {
      company_insurances: [],
      accessible_users: { dmes: [], s3_users: [] }
    }
  });

  React.useEffect(() => {
    if (patientId && !isSeeded) {
      getPatientTasks(patientId);
    }
  }, [patientId, isSeeded]);

  const order = isSeeded
    ? seed.order ?? {}
    : fetchOrderProfileApi?.response;
  const isFetchingOrder = isSeeded ? false : fetchOrderProfileApi?.isFetching;
  // The line-item consumers read this whole object, so the seed is folded into
  // the same shape rather than exposed alongside it.
  const lineItemsApi = isSeeded
    ? {
        ...fetchOrderLineItemsApi,
        response: seed.lineItems ?? [],
        hasFetched: true
      }
    : fetchOrderLineItemsApi;
  return (
    <OrderProfileContext.Provider
      value={{
        order,
        fetchOrderProfile: fetchOrderProfileApi.fetchData,
        isFetchingOrder,
        tasks: isSeeded ? seed.tasks ?? [] : tasks,
        createTask,
        createTaskNote,
        getPatientTasks,
        updateTask,
        isFetchingTasks,
        updateOrder: updateOrderProfileApi.fetchData,
        isUpdatingOrder: updateOrderProfileApi.isFetching,
        fetchOrderLineItemsApi: lineItemsApi,
        createOrderNoteApi,
        fetchOrderNotesApi,
        updateTrackingApi,
        addOrderLineItemByIdApi,
        addOrderLineItemApi,
        updateOrderLineItemByIdApi,
        fetchOrderEquipmentApi,
        patientFormAttributesAPI
      }}
    >
      {children}
    </OrderProfileContext.Provider>
  );
};

OrderProfileNewProvider.propTypes = {
  orderId: PropTypes.string,
  // Pre-fetched data for this order. When given, the provider makes no requests.
  seed: PropTypes.shape({
    order: PropTypes.object,
    lineItems: PropTypes.array,
    tasks: PropTypes.array
  }),
  children: PropTypes.node
};

export default OrderProfileNewProvider;
